import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ConfigurationDef } from './configuration-def.model';
import { ApiConfigurationsService } from '../../../services/configurations/api/api-configurations.service';
import { ConfigurationsResultsResponse } from '../../../models/configurations/responses/api-configurations-results-response.model';
import { ApplicationsService } from '../../../services/applications/applications.service';
import { AlertsService } from '../../../services/alerts.service';
import { ModalsService } from '../../../services/modals.service';

@Component({
  selector: 'app-configurations-block',
  templateUrl: 'configurations-block.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, CollapseModule, TooltipModule]
})
export class ConfigurationsBlockComponent implements OnInit, OnDestroy, OnChanges {

  @Input() configurations: ConfigurationDef[] = [];
  @Input() collapsible: boolean = true;
  @Input() serverMode: boolean = false;
  @Input() clientMode: boolean = false;

  get discoveryMode(): boolean {
    return this.serverMode || this.clientMode;
  }

  isOpen: boolean = false;
  isLoading: boolean = false;
  isSaving: boolean = false;

  valuesGlobal: Record<string, any> = {};
  valuesApp: Record<string, any> = {};
  appHasOverride: Record<string, boolean> = {};
  globalExists: Record<string, boolean> = {};

  private snapshotGlobal: Record<string, any> = {};
  private snapshotApp: Record<string, any> = {};
  private appChangeSub: Subscription | undefined;

  get hasPendingChanges(): boolean {
    return this.configurations.some(
      def => this.valuesGlobal[def.name] !== this.snapshotGlobal[def.name]
          || this.valuesApp[def.name] !== this.snapshotApp[def.name]
    );
  }

  constructor(
    private apiConfigurationsService: ApiConfigurationsService,
    public applicationsService: ApplicationsService,
    private alertsService: AlertsService,
    private modalsService: ModalsService
  ) {}

  ngOnInit(): void {
    this.refresh();
    this.appChangeSub = this.applicationsService.applicationIdSelectedSubject.subscribe(() => {
      this.refresh();
    });
  }

  ngOnChanges(): void {
    this.refresh();
  }

  ngOnDestroy(): void {
    this.appChangeSub?.unsubscribe();
  }

  onClickOverride(name: string): void {
    this.appHasOverride[name] = true;
    this.valuesApp[name] = this.valuesGlobal[name];
  }

  onBoolChange(record: Record<string, any>, name: string, checked: boolean): void {
    record[name] = checked ? 1 : 0;
  }

  onClickSave(): void {
    const appId = this.applicationsService.applicationIdSelected;
    const calls: Promise<void>[] = [];

    this.configurations.forEach(def => {
      const globalVal = def.type === 'bool' ? Number(this.valuesGlobal[def.name]) : this.valuesGlobal[def.name];
      const appVal = def.type === 'bool' ? Number(this.valuesApp[def.name]) : this.valuesApp[def.name];

      if (globalVal !== this.snapshotGlobal[def.name]) {
        calls.push(this.apiConfigurationsService.editConfiguration(def.name, def.type === 'string' ? String(globalVal) : '', def.type !== 'string' ? Number(globalVal) : 0, 0));
      }
      if (appVal !== this.snapshotApp[def.name] && this.appHasOverride[def.name]) {
        calls.push(this.apiConfigurationsService.editConfiguration(def.name, def.type === 'string' ? String(appVal) : '', def.type !== 'string' ? Number(appVal) : 0, appId));
      }
    });

    this.isSaving = true;
    Promise.all(calls).then(
      () => {
        this.isSaving = false;
        this.alertsService.displayAlertSuccess('Configurations saved!');
        this.refresh();
      },
      error => {
        this.isSaving = false;
        this.alertsService.displayAlertError('Error saving configurations: ' + error);
      }
    );
  }

  onClickDeleteOverride(name: string): void {
    this.modalsService.showConfirmModal(() => {
      const appId = this.applicationsService.applicationIdSelected;
      this.apiConfigurationsService.deleteApplicationConfiguration(name, appId).then(
        () => {
          this.alertsService.displayAlertSuccess('Application configuration deleted!');
          this.refresh();
        },
        error => { this.alertsService.displayAlertError('Error deleting configuration: ' + error); }
      );
    });
  }

  private refresh(): void {
    if (this.serverMode) {
      this.refreshFromDiscovery(() => this.apiConfigurationsService.getServerConfigurations());
      return;
    }
    if (this.clientMode) {
      this.refreshFromDiscovery(() => this.apiConfigurationsService.getClientConfigurations());
      return;
    }
    if (!this.configurations.length) return;
    this.isLoading = true;
    const keys = this.configurations.map(d => d.name);

    this.configurations.forEach(def => {
      this.valuesGlobal[def.name] = def.type === 'bool' ? 0 : (def.type === 'string' ? '' : 0);
      this.snapshotGlobal[def.name] = this.valuesGlobal[def.name];
      this.valuesApp[def.name] = def.type === 'bool' ? 0 : (def.type === 'string' ? '' : 0);
      this.snapshotApp[def.name] = this.valuesApp[def.name];
      this.appHasOverride[def.name] = false;
      this.globalExists[def.name] = true;
    });

    this.apiConfigurationsService.getConfigurations(keys).then(
      response => {
        this.isLoading = false;
        response.globalResults.forEach(config => {
          const def = this.configurations.find(d => d.name === config.name);
          const val = def?.type === 'string' ? config.valueText : config.valueInteger;
          this.valuesGlobal[config.name] = val;
          this.snapshotGlobal[config.name] = val;
          if (def && config.description) { def.description = config.description; }
        });
        response.applicationResults.forEach(config => {
          const def = this.configurations.find(d => d.name === config.name);
          const val = def?.type === 'string' ? config.valueText : config.valueInteger;
          this.valuesApp[config.name] = val;
          this.snapshotApp[config.name] = val;
          this.appHasOverride[config.name] = true;
        });
      },
      error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading configurations: ' + error);
      }
    );
  }

  private refreshFromDiscovery(loader: () => Promise<ConfigurationsResultsResponse>): void {
    this.isLoading = true;

    loader().then(
      response => {
        this.isLoading = false;

        const allNames = new Set<string>([
          ...response.globalResults.map(c => c.name),
          ...response.applicationResults.map(c => c.name),
        ]);

        this.configurations = Array.from(allNames).sort().map(name => {
          const sourceConfig = response.globalResults.find(c => c.name === name)
            ?? response.applicationResults.find(c => c.name === name);
          const type: 'string' | 'number' = (sourceConfig?.valueText ?? '') !== '' ? 'string' : 'number';
          const description = sourceConfig?.description || undefined;
          return { name, label: name, type, description };
        });

        this.configurations.forEach(def => {
          this.valuesGlobal[def.name] = def.type === 'string' ? '' : 0;
          this.snapshotGlobal[def.name] = this.valuesGlobal[def.name];
          this.valuesApp[def.name] = def.type === 'string' ? '' : 0;
          this.snapshotApp[def.name] = this.valuesApp[def.name];
          this.appHasOverride[def.name] = false;
          this.globalExists[def.name] = false;
        });

        response.globalResults.forEach(config => {
          const def = this.configurations.find(d => d.name === config.name);
          const val = def?.type === 'string' ? config.valueText : config.valueInteger;
          this.valuesGlobal[config.name] = val;
          this.snapshotGlobal[config.name] = val;
          this.globalExists[config.name] = true;
        });

        response.applicationResults.forEach(config => {
          const def = this.configurations.find(d => d.name === config.name);
          const val = def?.type === 'string' ? config.valueText : config.valueInteger;
          this.valuesApp[config.name] = val;
          this.snapshotApp[config.name] = val;
          this.appHasOverride[config.name] = true;
        });
      },
      error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading server configurations: ' + error);
      }
    );
  }
}
